import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Eye, Download, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "catalyst-ui-kit/badge";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "catalyst-ui-kit/dialog";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import {
  confirmInvoice,
  fetchSalesInvoices,
  previewInvoicePdf,
} from "services/invoices.service";
import { formatInvoicePublicNumber } from "utils/invoice-number";
import {
  invoiceErrorMessage,
  invoiceEmailSkipMessage,
} from "utils/invoice-errors";
import { API_URL } from "constants.js";
import { formatPeriodMonth } from "features/billing-period/billing-period";
import {
  btnPrimaryCls,
  btnSecondaryCls,
  btnSlimCls,
  btnSlimPrimaryCls,
} from "components/tw/classes";
import SendStatusBadge from "features/invoice-send/send-status-badge";
import SendInvoiceButton from "features/invoice-send/send-invoice-button";
import SendByEmailCheckbox from "features/invoice-send/send-by-email-checkbox";
import useRememberedSendChoice from "features/invoice-send/use-remembered-send-choice";
import type { InvoiceSendStatus } from "types/Invoice";
import useIsPhone from "features/files/use-is-phone";
import useOpenInvoiceModal from "./open-invoice-modal";
import SendFilterChips, {
  filterBySendStatus,
  type SendFilter,
} from "./send-filter-chips";

export type DraftInvoiceRow = {
  id: string;
  date: string;
  paymentDueDate?: string;
  number?: string | null;
  _number?: string | null;
  customer: string;
  customerName: string;
  order?: string | null;
  /** The contract number the customer holds; null when the order has none. */
  contractRef?: string | null;
  /** The billed month (YYYY-MM-DD); null for one-off invoices. */
  periodFrom?: string | null;
  periodTo?: string | null;
  _amount?: number;
  _amountWithVat?: number;
  hasInvoiceEmail?: boolean;
  invoiceEmail?: string | null;
  seriesTitle?: string | null;
  file?: string | null;
  status?: string;
  send: InvoiceSendStatus;
};

type BulkResult = {
  id: string;
  customerName: string;
  ok: boolean;
  emailSent?: boolean;
  message?: string;
  /** Never attempted — the run stopped after an earlier failure. */
  skipped?: boolean;
};

type Props = {
  status?: "draft" | "confirmed";
};

/**
 * Confirm order for "Confirm all": ascending by date, so a dated number
 * format (daily/monthly/yearly) numbers a batch chronologically instead of
 * moving the series' `currentPeriod` forward on the newest draft first and
 * then refusing every older one with `INVOICE_DATE_BEFORE_LAST_ISSUED`.
 *
 * `fetch-sales` sorts `date DESC` and does not expose `createdAt`, so two
 * drafts dated the same day cannot be told apart by when they were created —
 * a stable sort keeps them in the order the server gave them (their relative
 * order under `date DESC`) rather than reordering same-day drafts arbitrarily.
 */
export function sortDraftsForConfirm<T extends { date?: string | null }>(
  invoices: T[]
): T[] {
  return [...invoices].sort((a, b) => {
    const ad = a.date || "";
    const bd = b.date || "";
    if (ad < bd) return -1;
    if (ad > bd) return 1;
    return 0;
  });
}

/** Whether the customer has an invoice email, and which one. */
function InvoiceEmailBadge({ item }: { item: DraftInvoiceRow }) {
  const { t } = useTranslation();
  return item.hasInvoiceEmail ? (
    <Badge color="green" className="max-md:break-all">
      {item.invoiceEmail || t("yes", { defaultValue: "Yes" })}
    </Badge>
  ) : (
    <Badge color="amber">
      {t("invoice_email_missing", {
        defaultValue: "No invoice email",
      })}
    </Badge>
  );
}

/** Outgoing sales invoices list — drafts (confirm) or completed. */
export default function SalesInvoicesDraftsPanel({
  status = "draft",
}: Props) {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isPhone = useIsPhone();
  const [invoices, setInvoices] = useState<DraftInvoiceRow[]>([]);

  /** The billed month of a recurring invoice; blank for one-offs. */
  const periodColumn = {
    key: "periodFrom",
    name: t("invoice_period_column"),
    type: "custom" as const,
    render: (item: DraftInvoiceRow) =>
      item.periodFrom ? formatPeriodMonth(item.periodFrom) : "",
  };
  /**
   * The buyer. On a phone card it sits at the top right, across from the
   * number: right-aligned with a gap and breakable, so a long name never runs
   * into "Draft" beside it (the number and "Draft" are `whitespace-nowrap`:
   * the buyer is what wraps). The table keeps it as plain text.
   */
  const customerColumn = {
    key: "customerName",
    name: t("customer"),
    type: "custom" as const,
    mobileView: "topRight" as const,
    render: (item: DraftInvoiceRow) => (
      <span className="max-md:block max-md:pl-3 max-md:text-right max-md:[overflow-wrap:anywhere]">
        {item.customerName || "—"}
      </span>
    ),
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkResults, setBulkResults] = useState<BulkResult[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // One tick for the whole "Confirm all" run, remembered between visits.
  const [sendByEmail, setSendByEmail] = useRememberedSendChoice();
  const [sendFilter, setSendFilter] = useState<SendFilter>("all");
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const isDraft = status === "draft";

  // Switching tabs reuses this panel with another `status`; only the latest
  // request may write, or a slower Drafts response could fill the Complete tab.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!companyId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchSalesInvoices({ companyId, status });
      if (seq !== requestSeq.current) return;
      setInvoices(data.invoices || []);
    } catch (e: any) {
      if (seq !== requestSeq.current) return;
      setError(
        e?.response?.data?.message ||
          e?.message ||
          t("error", { defaultValue: "Error" })
      );
      setInvoices([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openInvoiceModal = useOpenInvoiceModal();
  const openInvoice = (invoice: DraftInvoiceRow) =>
    openInvoiceModal(invoice, { isDraft, onDone: load });

  const confirmDeleteDraft = (invoice: DraftInvoiceRow) => {
    dispatch(
      setModalxOptions({
        title: t("delete_draft_invoice", {
          defaultValue: "Delete draft invoice",
        }),
        size: "small",
      })
    );
    dispatch(setModalxComponent("DeleteDraftInvoiceDialog"));
    dispatch(
      setModalxComponentProps({
        invoiceId: invoice.id,
        customerName: invoice.customerName,
        onDone: load,
      })
    );
  };

  const handlePdfView = (fileId: string) => {
    if (!fileId || !companyId) return;
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  const pdfUrl = (fileId?: string | null) =>
    fileId && companyId
      ? `${API_URL}/${companyId}/files/download/${fileId}`
      : null;

  const previewDraft = async (invoice: DraftInvoiceRow) => {
    if (!companyId) return;
    setPreviewingId(invoice.id);
    try {
      await previewInvoicePdf({ companyId, invoiceId: invoice.id });
    } catch (e: any) {
      toast.error(invoiceErrorMessage(e, t));
    } finally {
      setPreviewingId(null);
    }
  };

  const openBulkModal = () => {
    setBulkResults(null);
    setBulkProgress("");
    setSelectedIds(invoices.map((inv) => inv.id));
    setBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    if (bulkConfirming) return;
    setBulkModalOpen(false);
    setBulkResults(null);
    setBulkProgress("");
    setSelectedIds([]);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllInModal = () => {
    setSelectedIds(invoices.map((inv) => inv.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const selectedInvoices = invoices.filter((inv) =>
    selectedIds.includes(inv.id)
  );

  const handleConfirmAll = async () => {
    if (!companyId || !selectedInvoices.length) return;
    setBulkConfirming(true);
    setBulkResults(null);
    const results: BulkResult[] = [];
    // Ascending by date (see `sortDraftsForConfirm`) — confirming newest-dated
    // first moves a dated series' `currentPeriod` forward on that draft, then
    // refuses every older selected one with INVOICE_DATE_BEFORE_LAST_ISSUED.
    const toConfirm = sortDraftsForConfirm(selectedInvoices);
    let stoppedAt = -1;

    for (let i = 0; i < toConfirm.length; i += 1) {
      const inv = toConfirm[i];
      setBulkProgress(
        t("sales_invoices_bulk_progress", {
          defaultValue: "Confirming {{current}} of {{total}}…",
          current: i + 1,
          total: toConfirm.length,
        })
      );
      try {
        const result = await confirmInvoice({
          companyId,
          invoiceId: inv.id,
          send: sendByEmail,
        });
        const mapped = invoiceEmailSkipMessage(
          t,
          result.emailSkipReason,
          !!result.emailSent,
          result.emailError
        );
        results.push({
          id: inv.id,
          customerName: inv.customerName,
          ok: mapped.ok,
          emailSent: !!result.emailSent,
          message: mapped.message,
        });
      } catch (e: any) {
        results.push({
          id: inv.id,
          customerName: inv.customerName,
          ok: false,
          message: invoiceErrorMessage(e, t),
        });
        // Stop rather than keep confirming: a date-based series that just
        // refused one draft (its currentPeriod already moved past it) would
        // very likely refuse the rest of an ascending batch too, and running
        // on could confirm out-of-order invoices around the failure.
        stoppedAt = i + 1;
        break;
      }
    }

    if (stoppedAt >= 0) {
      for (const inv of toConfirm.slice(stoppedAt)) {
        results.push({
          id: inv.id,
          customerName: inv.customerName,
          ok: false,
          skipped: true,
          message: t("sales_invoices_bulk_not_attempted", {
            defaultValue: "Not attempted — stopped after an earlier failure.",
          }),
        });
      }
    }

    setBulkResults(results);
    setBulkProgress("");
    setBulkConfirming(false);

    const okCount = results.filter((r) => r.ok).length;
    const failCount = results.filter((r) => !r.ok && !r.skipped).length;
    const skippedCount = results.filter((r) => r.skipped).length;
    if (failCount === 0 && skippedCount === 0) {
      toast.success(
        t("sales_invoices_bulk_success", {
          defaultValue: "{{count}} invoices confirmed",
          count: okCount,
        })
      );
    } else {
      toast.warning(
        t("sales_invoices_bulk_partial_stopped", {
          defaultValue:
            "{{ok}} confirmed, {{fail}} failed, {{skipped}} not attempted",
          ok: okCount,
          fail: failCount,
          skipped: skippedCount,
        })
      );
    }
    await load();
  };

  const missingEmailCount = selectedInvoices.filter(
    (i) => !i.hasInvoiceEmail
  ).length;

  const visibleInvoices = isDraft
    ? invoices
    : filterBySendStatus(invoices, sendFilter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-500">
          {isDraft
            ? t("sales_invoices_confirmation_hint", {
                defaultValue:
                  "Draft invoices created on the schedule day. Confirm to assign a number; emailing the customer is optional.",
              })
            : t("outgoing_invoices_complete_hint", {
                defaultValue:
                  "Confirmed sales invoices with assigned numbers. Send them to customers from here.",
              })}
        </p>
        {isDraft && !loading && invoices.length > 0 && (
          <button type="button" className={btnPrimaryCls} onClick={openBulkModal}>
            {t("sales_invoices_confirm_all", {
              defaultValue: "Confirm all",
            })}
          </button>
        )}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
          <button
            type="button"
            className="ml-2 text-xs underline"
            onClick={() => setError("")}
          >
            {t("close")}
          </button>
        </div>
      ) : null}

      {!isDraft && !loading && invoices.length > 0 && (
        <SendFilterChips
          rows={invoices}
          value={sendFilter}
          onChange={setSendFilter}
        />
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
        {loading ? (
          <div className="p-4">
            <DataTableSkeleton />
          </div>
        ) : invoices.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
              {isDraft
                ? t("sales_invoices_drafts_empty", {
                    defaultValue: "No draft sales invoices",
                  })
                : t("outgoing_invoices_complete_empty", {
                    defaultValue: "No confirmed sales invoices",
                  })}
            </p>
            <p className="mt-2 text-sm text-zinc-500">
              {isDraft
                ? t("sales_invoices_drafts_empty_body", {
                    defaultValue:
                      "When auto-schedule creates drafts for signed orders, they will appear here for confirmation.",
                  })
                : t("outgoing_invoices_complete_empty_body", {
                    defaultValue:
                      "Confirmed invoices will appear here after you confirm drafts.",
                  })}
            </p>
            <button
              type="button"
              className={`${btnSecondaryCls} mt-4`}
              onClick={load}
            >
              {t("refresh", { defaultValue: "Refresh" })}
            </button>
          </div>
        ) : (
          <div className="p-2">
            <DataTable
              collection={visibleInvoices}
              columns={
                isDraft
                  ? [
                      { key: "#" },
                      {
                        key: "_number",
                        name: t("invoice_number", {
                          defaultValue: "Invoice number",
                        }),
                        type: "custom",
                        mobileView: "topLeft",
                        render: (item: DraftInvoiceRow) => (
                          <span className="whitespace-nowrap">
                            {item._number ||
                              t("draft", { defaultValue: "Draft" })}
                          </span>
                        ),
                      },
                      customerColumn,
                      {
                        key: "contractRef",
                        name: t("invoice_contract", {
                          defaultValue: "Contract",
                        }),
                        type: "custom",
                        render: (item: DraftInvoiceRow) =>
                          item.contractRef || "—",
                      },
                      {
                        key: "date",
                        name: t("date"),
                        type: "custom",
                        mobileView: "bottomLeft",
                        render: (item: DraftInvoiceRow) => (
                          <div>
                            {item.date ? (
                              <PrettyDate dateString={item.date} />
                            ) : (
                              "—"
                            )}
                            {/* Datatable's phone card has 4 slots; this one
                                also carries the amount and whether the
                                customer has an invoice email, so nothing
                                needs a 5th. The table keeps the date alone. */}
                            {isPhone && (
                              <div className="mt-0.5 space-y-1 text-xs text-zinc-500">
                                <Currency
                                  value={item._amountWithVat || 0}
                                  isSymbol
                                />
                                <div>
                                  <InvoiceEmailBadge item={item} />
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      },
                      periodColumn,
                      {
                        key: "_amountWithVat",
                        name: t("totalWithVat", {
                          defaultValue: "Total with VAT",
                        }),
                        type: "custom",
                        render: (item: DraftInvoiceRow) => (
                          <Currency
                            value={item._amountWithVat || 0}
                            isSymbol
                          />
                        ),
                      },
                      {
                        key: "hasInvoiceEmail",
                        name: t("email"),
                        type: "custom",
                        render: (item: DraftInvoiceRow) => (
                          <InvoiceEmailBadge item={item} />
                        ),
                      },
                      {
                        key: "actions",
                        name: t("actions", { defaultValue: "Actions" }),
                        type: "custom",
                        mobileView: "bottomRight",
                        render: (item: DraftInvoiceRow) => (
                          // On a phone the card's half-width corner is too
                          // narrow for one row: the main action takes the
                          // first line, the icons wrap under it.
                          <div className="flex flex-wrap items-center gap-2 max-md:justify-end">
                            <button
                              type="button"
                              className={btnSlimCls}
                              title={t("invoice_preview_pdf", {
                                defaultValue: "Preview PDF",
                              })}
                              disabled={previewingId === item.id}
                              onClick={() => previewDraft(item)}
                            >
                              <Eye className="inline size-3.5" />
                            </button>
                            <button
                              type="button"
                              className={btnSlimCls}
                              title={t("edit")}
                              onClick={() => openInvoice(item)}
                            >
                              <Pencil className="inline size-3.5" />
                            </button>
                            <button
                              type="button"
                              className={`${btnSlimPrimaryCls} max-md:order-first max-md:w-full max-md:justify-center`}
                              onClick={() => openInvoice(item)}
                            >
                              {t("confirm_invoice", {
                                defaultValue: "Confirm",
                              })}
                            </button>
                            {/* Deleting a draft is the only way to release its
                                order back into the create-invoice list. */}
                            <button
                              type="button"
                              className={btnSlimCls}
                              title={t("delete", { defaultValue: "Delete" })}
                              onClick={() => confirmDeleteDraft(item)}
                            >
                              <Trash2 className="inline size-3.5 text-red-600" />
                            </button>
                          </div>
                        ),
                      },
                    ]
                  : [
                      { key: "#" },
                      {
                        key: "send",
                        name: t("invoice_send_column", {
                          defaultValue: "Emailing",
                        }),
                        type: "custom",
                        render: (item: DraftInvoiceRow) => (
                          <SendStatusBadge send={item.send} showDetails />
                        ),
                      },
                      {
                        key: "_number",
                        name: t("invoice_number", {
                          defaultValue: "Invoice number",
                        }),
                        type: "custom",
                        mobileView: "topLeft",
                        render: (item: DraftInvoiceRow) => (
                          <span className="whitespace-nowrap">
                            {formatInvoicePublicNumber(item) || "—"}
                          </span>
                        ),
                      },
                      customerColumn,
                      {
                        key: "contractRef",
                        name: t("invoice_contract", {
                          defaultValue: "Contract",
                        }),
                        type: "custom",
                        render: (item: DraftInvoiceRow) =>
                          item.contractRef || "—",
                      },
                      {
                        key: "date",
                        name: t("date"),
                        type: "custom",
                        mobileView: "bottomLeft",
                        render: (item: DraftInvoiceRow) => (
                          <div>
                            {item.date ? (
                              <PrettyDate dateString={item.date} />
                            ) : (
                              "—"
                            )}
                            {/* See the Draft tab's `date` column: the phone
                                card only has 4 slots, so this one also
                                carries the amount and the send status. */}
                            {isPhone && (
                              <div className="mt-0.5 space-y-1 text-xs text-zinc-500">
                                <Currency
                                  value={item._amountWithVat || 0}
                                  isSymbol
                                />
                                <SendStatusBadge send={item.send} />
                              </div>
                            )}
                          </div>
                        ),
                      },
                      periodColumn,
                      {
                        key: "_amountWithVat",
                        name: t("totalWithVat", {
                          defaultValue: "Total with VAT",
                        }),
                        type: "custom",
                        render: (item: DraftInvoiceRow) => (
                          <Currency
                            value={item._amountWithVat || 0}
                            isSymbol
                          />
                        ),
                      },
                      {
                        key: "actions",
                        name: t("actions", { defaultValue: "Actions" }),
                        type: "custom",
                        mobileView: "bottomRight",
                        render: (item: DraftInvoiceRow) => (
                          <div className="flex flex-wrap items-center gap-2 max-md:justify-end">
                            <button
                              type="button"
                              className={btnSlimCls}
                              title={t("view")}
                              onClick={() => {
                                if (item.file) handlePdfView(item.file);
                                else openInvoice(item);
                              }}
                            >
                              <Eye className="inline size-3.5" />
                            </button>
                            <button
                              type="button"
                              className={btnSlimCls}
                              title={t("download")}
                              onClick={() => {
                                const url = pdfUrl(item.file);
                                if (url) window.open(url, "_blank");
                              }}
                            >
                              <Download className="inline size-3.5" />
                            </button>
                            <button
                              type="button"
                              className={btnSlimCls}
                              onClick={() => openInvoice(item)}
                            >
                              {t("view_invoice", { defaultValue: "View" })}
                            </button>
                            <SendInvoiceButton
                              invoice={item}
                              onSent={load}
                              className="max-md:order-first max-md:w-full max-md:justify-center"
                            />
                          </div>
                        ),
                      },
                    ]
              }
            />
          </div>
        )}
      </div>

      {isDraft && (
        <Dialog
          open={bulkModalOpen}
          onClose={() => {
            if (!bulkConfirming) closeBulkModal();
          }}
          size="4xl"
        >
          <DialogTitle>
            {t("sales_invoices_confirm_all_title", {
              defaultValue: "Confirm all sales invoices",
            })}
          </DialogTitle>
          <DialogBody>
            <div className="space-y-4">
              {!bulkResults && (
                <>
                  <p className="text-sm text-zinc-600 dark:text-zinc-300">
                    {t("sales_invoices_confirm_all_body", {
                      defaultValue:
                        "Confirm {{count}} selected draft invoice(s): each gets a number and a PDF. Tick the box below to email the customers as well. Uncheck any you want to leave as draft.",
                      count: selectedIds.length,
                    })}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className={btnSlimCls}
                      onClick={selectAllInModal}
                      disabled={bulkConfirming}
                    >
                      {t("select_all", { defaultValue: "Select all" })}
                    </button>
                    <button
                      type="button"
                      className={btnSlimCls}
                      onClick={clearSelection}
                      disabled={bulkConfirming}
                    >
                      {t("deselect_all", { defaultValue: "Deselect all" })}
                    </button>
                    <span className="text-xs text-zinc-500">
                      {t("sales_invoices_bulk_selected_count", {
                        defaultValue: "{{selected}} of {{total}} selected",
                        selected: selectedIds.length,
                        total: invoices.length,
                      })}
                    </span>
                  </div>
                  <SendByEmailCheckbox
                    plural
                    checked={sendByEmail}
                    onChange={setSendByEmail}
                    disabled={bulkConfirming}
                  />
                  {sendByEmail && missingEmailCount > 0 && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-500/10 dark:text-amber-200">
                      {t("sales_invoices_bulk_missing_email_warn", {
                        defaultValue:
                          "{{count}} selected invoice(s) have no invoice email — they will still be confirmed but not emailed.",
                        count: missingEmailCount,
                      })}
                    </div>
                  )}
                  {bulkProgress ? (
                    <p className="text-sm text-zinc-600">{bulkProgress}</p>
                  ) : null}
                  <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
                    <table className="w-full min-w-[520px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800/60">
                          <th className="px-3 py-2 w-10">
                            {t("select", { defaultValue: "Select" })}
                          </th>
                          <th className="px-3 py-2">
                            {t("invoice_number", {
                              defaultValue: "Invoice number",
                            })}
                          </th>
                          <th className="px-3 py-2">{t("customer")}</th>
                          <th className="px-3 py-2">
                            {t("invoice_contract", {
                              defaultValue: "Contract",
                            })}
                          </th>
                          <th className="px-3 py-2 text-right">
                            {t("totalWithVat", {
                              defaultValue: "Total with VAT",
                            })}
                          </th>
                          <th className="px-3 py-2">{t("email")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((item) => {
                          const selected = selectedIds.includes(item.id);
                          return (
                            // On a phone the whole row selects, because a 16 px
                            // checkbox is hard to hit with a finger. On a desktop
                            // only the checkbox does, so copying a customer name
                            // by triple click cannot flip the row.
                            <tr
                              key={item.id}
                              className="border-b border-zinc-100 last:border-0 max-md:cursor-pointer dark:border-zinc-800"
                              onClick={
                                isPhone
                                  ? () => {
                                      if (!bulkConfirming) toggleSelected(item.id);
                                    }
                                  : undefined
                              }
                            >
                              <td className="px-3 py-2.5 max-md:py-3">
                                <input
                                  type="checkbox"
                                  className="size-4 accent-indigo-600"
                                  checked={selected}
                                  disabled={bulkConfirming}
                                  onChange={() => toggleSelected(item.id)}
                                  // The click must not reach the phone row, which
                                  // would toggle it a second time.
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label={t("select", {
                                    defaultValue: "Select",
                                  })}
                                />
                              </td>
                              <td className="px-3 py-2.5 font-medium text-zinc-800 dark:text-zinc-100">
                                {item._number ||
                                  t("draft", { defaultValue: "Draft" })}
                              </td>
                              <td className="px-3 py-2.5 font-medium text-zinc-800 dark:text-zinc-100">
                                {item.customerName || "—"}
                              </td>
                              <td className="px-3 py-2.5 text-zinc-600 dark:text-zinc-300">
                                {item.contractRef || "—"}
                              </td>
                              <td className="px-3 py-2.5 text-right text-zinc-800 dark:text-zinc-100">
                                <Currency
                                  value={item._amountWithVat || 0}
                                  isSymbol
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                {item.hasInvoiceEmail ? (
                                  <Badge color="green">
                                    {item.invoiceEmail || "—"}
                                  </Badge>
                                ) : (
                                  <Badge color="amber">
                                    {t("invoice_email_missing", {
                                      defaultValue: "No invoice email",
                                    })}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {bulkResults && (
                <>
                  <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                    {t("sales_invoices_bulk_results", {
                      defaultValue: "Results",
                    })}
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800/60">
                          <th className="px-3 py-2 w-10">#</th>
                          <th className="px-3 py-2">{t("customer")}</th>
                          <th className="px-3 py-2">
                            {t("status", { defaultValue: "Status" })}
                          </th>
                          <th className="px-3 py-2">
                            {t("details", { defaultValue: "Details" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResults.map((item, index) => (
                          <tr
                            key={item.id}
                            className="border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                          >
                            <td className="px-3 py-2.5 text-zinc-500">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2.5 font-medium text-zinc-800 dark:text-zinc-100">
                              {item.customerName}
                            </td>
                            <td className="px-3 py-2.5">
                              {item.ok ? (
                                <Badge color="green">
                                  {t("done", { defaultValue: "Done" })}
                                </Badge>
                              ) : item.skipped ? (
                                <Badge color="zinc">
                                  {t("sales_invoices_bulk_skipped", {
                                    defaultValue: "Not attempted",
                                  })}
                                </Badge>
                              ) : (
                                <Badge color="red">
                                  {t("failed", { defaultValue: "Failed" })}
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-zinc-600 dark:text-zinc-300">
                              {item.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </DialogBody>
          <DialogActions>
            {bulkResults ? (
              <button
                type="button"
                className={btnPrimaryCls}
                onClick={closeBulkModal}
              >
                {t("close")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={btnSecondaryCls}
                  onClick={closeBulkModal}
                  disabled={bulkConfirming}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className={btnPrimaryCls}
                  onClick={handleConfirmAll}
                  disabled={bulkConfirming || selectedIds.length === 0}
                >
                  {bulkConfirming
                    ? t("loading", { defaultValue: "Loading…" })
                    : t("sales_invoices_confirm_selected", {
                        defaultValue: "Confirm selected ({{count}})",
                        count: selectedIds.length,
                      })}
                </button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
